import { type ApiEnvelope, api, unwrap } from "@/lib/api";

export type AiProductJobStatus = "queued" | "running" | "done" | "failed";

export interface AiProductJob {
	id: string;
	rawText: string;
	imageUrls: string[];
	autoPublish: boolean;
	status: AiProductJobStatus;
	step: string | null;
	progress: number;
	warnings: string[];
	error: string | null;
	aiDraft: Record<string, unknown> | null;
	productId: string | null;
	productTitle: string | null;
	productStatus: string | null;
	createdById: string;
	createdByLabel: string;
	createdAt: string;
	updatedAt: string;
	startedAt: string | null;
	finishedAt: string | null;
}

export interface AiProductJobStats {
	queued: number;
	running: number;
	done: number;
	failed: number;
	doneToday: number;
}

export interface AiProductJobList {
	data: AiProductJob[];
	total: number;
	page: number;
	pageSize: number;
}

export interface BulkCreateResultDetail {
	detected: number;
	queued: number;
	skipped: number;
	/** Fiches écartées parce qu'elles figurent déjà au catalogue. */
	dejaImportes: number;
	/** Fiches nouvelles laissées de côté faute de place dans la file. */
	filePleine: number;
}

export interface SuggestedCategory {
	slug: string;
	name: string;
	parentId: string | null;
	/** Chemin lisible du parent, pour montrer où la catégorie va se ranger. */
	parentPath: string | null;
	description: string;
	metaTitle: string;
	metaDescription: string;
	keywords: string[];
	/** Fiches en échec qui attendent exactement cette catégorie. */
	jobsEnAttente: number;
}

export interface CreateMissingCategoryPayload {
	slug: string;
	name: string;
	parentId?: string | null;
	description?: string;
	metaTitle?: string;
	metaDescription?: string;
	keywords?: string[];
}

export interface CreateAiProductJobPayload {
	rawText: string;
	imageUrls?: string[];
	autoPublish?: boolean;
}

export interface BulkCreateResult {
	created: AiProductJob[];
	detected: number;
	queued: number;
	skipped: number;
}

const toQs = (f: Record<string, unknown>): string => {
	const usp = new URLSearchParams();
	for (const [k, v] of Object.entries(f)) {
		if (v === undefined || v === null || v === "") continue;
		usp.set(k, String(v));
	}
	const qs = usp.toString();
	return qs ? `?${qs}` : "";
};

export const aiProductApi = {
	create: (payload: CreateAiProductJobPayload) =>
		unwrap<AiProductJob>(
			api.post<ApiEnvelope<AiProductJob>>("/admin/ai/product-jobs", payload),
		),
	/**
	 * Met un lot de fiches en file.
	 *
	 * Délai d'attente allongé : la requête transporte jusqu'à un méga-octet de
	 * texte et le serveur enregistre les tâches une par une avant de répondre.
	 * Avec les quinze secondes appliquées par défaut, un gros lot expirait côté
	 * navigateur alors que la mise en file se poursuivait — l'écran annonçait un
	 * échec pour un import qui, lui, aboutissait.
	 */
	createBulk: (payload: { rawText: string; autoPublish?: boolean }) =>
		unwrap<BulkCreateResult>(
			api.post<ApiEnvelope<BulkCreateResult>>(
				"/admin/ai/product-jobs/bulk",
				payload,
				{ timeout: 120_000 },
			),
		),
	list: (
		filter: {
			status?: AiProductJobStatus;
			page?: number;
			pageSize?: number;
		} = {},
	) =>
		unwrap<AiProductJobList>(
			api.get<ApiEnvelope<AiProductJobList>>(
				`/admin/ai/product-jobs${toQs(filter)}`,
			),
		),
	/**
	 * Catégorie que l'analyse réclamait, proposée pour création.
	 *
	 * Délai d'attente allongé : la proposition passe par un appel au modèle, qui
	 * demande couramment quinze à trente secondes. Avec les quinze secondes
	 * appliquées par défaut, la fenêtre affichait « timeout of 15000ms
	 * exceeded » alors que la réponse arrivait juste après.
	 */
	suggestCategory: (id: string) =>
		unwrap<SuggestedCategory>(
			api.get<ApiEnvelope<SuggestedCategory>>(
				`/admin/ai/product-jobs/${id}/suggest-category`,
				{ timeout: 90_000 },
			),
		),

	// La création enchaîne l'écriture en base et la remise en file de toutes les
	// fiches concernées : plus lent qu'une simple écriture, sans atteindre la
	// durée d'un appel au modèle.
	createMissingCategory: (payload: CreateMissingCategoryPayload) =>
		unwrap<{ categoryId: string; slug: string; relances: number }>(
			api.post<ApiEnvelope<{ categoryId: string; slug: string; relances: number }>>(
				"/admin/ai/product-jobs/missing-category",
				payload,
				{ timeout: 60_000 },
			),
		),

	stats: () =>
		unwrap<AiProductJobStats>(
			api.get<ApiEnvelope<AiProductJobStats>>("/admin/ai/product-jobs/stats"),
		),
	get: (id: string) =>
		unwrap<AiProductJob>(
			api.get<ApiEnvelope<AiProductJob>>(`/admin/ai/product-jobs/${id}`),
		),
	retry: (id: string) =>
		unwrap<AiProductJob>(
			api.post<ApiEnvelope<AiProductJob>>(`/admin/ai/product-jobs/${id}/retry`),
		),
	remove: (id: string) =>
		api.delete(`/admin/ai/product-jobs/${id}`).then(() => true),
};
